/** Button: the kit's one button. `outline` sits on the page, `surface` on a card frame, `ghost` is a bare icon button, `primary` is the dark fill. */
import type { ComponentProps } from "react";
import { Slot } from "radix-ui";
import { cx, focusRing } from "./cx";

const BASE =
  "relative inline-flex shrink-0 items-center whitespace-nowrap font-medium select-none transition-[background-color,border-color,box-shadow,transform,color] duration-150 ease-out active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 [&_img]:pointer-events-none [&_img]:shrink-0";

const VARIANT = {
  outline:
    "rounded-lg border-hair border-border bg-card text-secondary-foreground hover:border-border-hover hover:bg-card-hover hover:shadow-lift data-[state=open]:border-border-hover data-[state=open]:bg-card-hover",
  surface: "rounded-lg border-hair border-input bg-card text-secondary-foreground hover:bg-card-hover hover:shadow-lift data-[state=open]:bg-card-hover",
  ghost: "rounded-md text-secondary-foreground hover:bg-black/[0.05]",
  primary: "rounded-lg border border-primary bg-[linear-gradient(180deg,var(--color-primary-from)_0%,var(--color-primary)_64.7%)] text-primary-foreground hover:brightness-110",
};

/** `default` leaves room for a trailing chevron, `lead` for a leading icon. */
const SIZE = {
  default: "h-9 gap-1.5 pl-3 pr-2.5 text-body leading-none",
  lead: "h-9 gap-1.5 pl-2.5 pr-3 text-body leading-none",
  sm: "h-8 gap-1.5 px-2.5 text-meta leading-none",
  icon: "size-9 p-2 leading-none",
  "icon-sm": "size-8 p-2 leading-none",
};

export interface ButtonProps extends ComponentProps<"button"> {
  variant?: keyof typeof VARIANT;
  size?: keyof typeof SIZE;
  justify?: "center" | "between";
  asChild?: boolean;
}

export function Button({ variant = "outline", size = "default", justify = "center", asChild = false, className, ...props }: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      data-slot="button"
      className={cx(BASE, focusRing, justify === "between" ? "justify-between" : "justify-center", VARIANT[variant], SIZE[size], className)}
      {...props}
    />
  );
}
